import { useCallback, useEffect, useRef, useState } from 'react';
import { useProject } from '@/features/project/hooks/use-projects';
import { goalsAutosaveCoordinator } from '@/features/project/lib/goals-autosave-coordinator';
import {
  convertFacadeArea,
  formatFacadeAreaValue,
  parseFacadeArea,
  type FacadeAreaUnit,
} from '@/features/project/ui/FacadeAreaField';

/**
 * Shared goals-editor buffer used by both the standalone goals page and the
 * embedded render-settings goals sheet.
 *
 * Since the goals-dialogue pivot, edits are NOT auto-saved per keystroke — the
 * user commits explicitly (a teal checkmark) so the server-side LLM evaluation
 * runs on a deliberate save, not mid-typing. `isDirty` drives the commit button
 * and the leave-confirm; after a successful commit the detail query refetches
 * and the server baseline catches up, dropping `isDirty` back to false.
 *
 * Server reconcile (mirrors Apple's `ProjectGoalsView.loadProject`): the buffer
 * seeds from the project and afterwards adopts a CHANGED server value only for
 * the fields the user has not touched (buffer still equals the previous server
 * baseline). That keeps the goals-wipe guard (2026-06-02) — a refetch can never
 * trample what someone is typing — while fixing the stale-brief freeze: the
 * render-settings sheet is mounted (and therefore seeded) while still closed, so
 * a seed-once buffer latched onto whatever the cache held at panel-mount time —
 * including a stale IndexedDB snapshot — and showed that text forever, even
 * after the refetch landed. Worse, committing that frozen buffer wrote the old
 * brief back over the newer server one.
 */
export function useGoalsEditor(projectId: string) {
  const { data: projectResponse, isLoading } = useProject(projectId);
  const project = projectResponse?.data;

  const serverGoals = project?.attributes.goals ?? '';
  const serverRegion = project?.attributes.construction_region ?? '';
  const serverArea =
    project?.attributes.facade_area != null
      ? String(project.attributes.facade_area)
      : '';
  const serverUnit: FacadeAreaUnit =
    project?.attributes.facade_area_unit ?? 'm2';

  const [goals, setGoals] = useState(serverGoals);
  const [region, setRegion] = useState(serverRegion);
  const [area, setArea] = useState(serverArea);
  // Local mutable display unit — the toggle flips it immediately, before the
  // server round-trips. Seeded from the server unit and kept in sync until the
  // user overrides it (same plain-buffer reconcile pattern as the value).
  const [unit, setUnit] = useState<FacadeAreaUnit>(serverUnit);
  // Last server snapshot the buffer was reconciled against. `null` until the
  // project first loads (the seed). A field whose buffer still equals this
  // baseline is untouched and may adopt a newer server value.
  const baselineRef = useRef<{
    goals: string;
    region: string;
    area: string;
    unit: FacadeAreaUnit;
  } | null>(null);

  // Only an actually-edited area rides along in the PATCH — sending the field
  // (even unchanged) marks it user-owned and clears the "estimated" flag.
  const areaChanged = area.trim() !== serverArea;
  const unitChanged = unit !== serverUnit;
  const pendingArea = areaChanged ? parseFacadeArea(area) : undefined;

  // Seed on first load, then reconcile per field: adopt a changed server value
  // only where the buffer still matches the previous baseline (nothing typed).
  useEffect(() => {
    if (!project) return;
    const next = {
      goals: serverGoals,
      region: serverRegion,
      area: serverArea,
      unit: serverUnit,
    };
    const baseline = baselineRef.current;
    baselineRef.current = next;

    if (!baseline) {
      setGoals(next.goals);
      setRegion(next.region);
      setArea(next.area);
      setUnit(next.unit);
      return;
    }

    if (next.goals !== baseline.goals && goals === baseline.goals) {
      setGoals(next.goals);
    }
    if (next.region !== baseline.region && region === baseline.region) {
      setRegion(next.region);
    }
    if (next.area !== baseline.area && area === baseline.area) {
      setArea(next.area);
    }
    if (next.unit !== baseline.unit && unit === baseline.unit) {
      setUnit(next.unit);
    }
  }, [
    project,
    serverGoals,
    serverRegion,
    serverArea,
    serverUnit,
    goals,
    region,
    area,
    unit,
  ]);

  const isDirty =
    goals.trim() !== serverGoals.trim() ||
    region.trim() !== serverRegion.trim() ||
    areaChanged ||
    unitChanged;

  /**
   * Switch the display unit: convert the current buffer value (if numeric),
   * update the unit, and persist immediately — sending BOTH the converted
   * `facade_area` and the new `facade_area_unit` so the server stores it in the
   * unit the user picked. An empty/invalid buffer still switches the unit
   * (preference persists) without sending a numeric value.
   */
  const changeUnit = useCallback(
    (next: FacadeAreaUnit) => {
      if (next === unit) return;
      const parsed = parseFacadeArea(area);
      let converted: number | null | undefined = parsed;
      let nextArea = area;
      if (typeof parsed === 'number') {
        const value = convertFacadeArea(parsed, unit, next);
        nextArea = formatFacadeAreaValue(value);
        converted = value;
        setArea(nextArea);
      }
      setUnit(next);
      goalsAutosaveCoordinator.flushNow(
        projectId,
        goals,
        region,
        converted,
        next
      );
    },
    [unit, area, projectId, goals, region]
  );

  /** Persist the current buffer (fires the server-side evaluation). */
  const commit = useCallback(() => {
    if (!isDirty) return;
    goalsAutosaveCoordinator.flushNow(
      projectId,
      goals,
      region,
      pendingArea,
      unitChanged ? unit : undefined
    );
  }, [isDirty, projectId, goals, region, pendingArea, unitChanged, unit]);

  /** Drop local edits back to the saved server baseline. */
  const discard = useCallback(() => {
    setGoals(serverGoals);
    setRegion(serverRegion);
    setArea(serverArea);
    setUnit(serverUnit);
  }, [serverGoals, serverRegion, serverArea, serverUnit]);

  return {
    project,
    isLoading,
    goals,
    setGoals,
    region,
    setRegion,
    area,
    setArea,
    unit,
    changeUnit,
    areaChanged,
    isDirty,
    commit,
    discard,
  };
}
